import OpenAI from 'openai';
import { BlogPost, BlogTopic } from '../types/blog';
import { API_KEYS } from '../utils/api-config';
import type { ExchangeRate } from '../types/currency';

const openai = new OpenAI({
  apiKey: API_KEYS.OPENAI_API_KEY,
  dangerouslyAllowBrowser: true
});

export async function generateBlogTopics(niche: string, count: number = 5): Promise<BlogTopic[]> {
  try {
    const response = await openai.chat.completions.create({
      model: "gpt-4",
      messages: [{
        role: "system",
        content: `You are a financial education content strategist specializing in currency markets and economics.

        CRITICAL RULES:
        - DO NOT generate topics about trading strategies, techniques, or methods
        - DO NOT suggest topics about currency pair trading (especially NGN pairs)
        - Focus on educational topics about market mechanics, economics, and financial literacy
        - All topics should be informational and educational, not instructional for trading`
      }, {
        role: "user",
        content: `Generate ${count} educational blog post topics about ${niche}.

        Topics should be educational and focus on:
        - Understanding how markets work
        - Economic concepts and principles
        - Financial literacy and education
        - Market analysis and interpretation (educational)
        - Historical context and trends

        DO NOT include topics about:
        - Trading strategies or systems
        - How to trade specific currency pairs
        - Buy/sell signals or setups
        - Trading techniques or methods

        Each topic should include a title and brief description.
        Return as JSON array.`
      }],
      temperature: 0.7,
    });

    const content = response.choices[0].message.content || '[]';
    return JSON.parse(content);
  } catch (error) {
    console.error('Error generating blog topics:', error);
    throw error;
  }
}

export async function generateBlogPost(topic: BlogTopic): Promise<BlogPost> {
  try {
    const response = await openai.chat.completions.create({
      model: "gpt-4",
      messages: [{
        role: "system",
        content: `You are a financial education writer specializing in currency markets and economics.

        CRITICAL RULES:
        - DO NOT provide trading advice, strategies, or recommendations
        - DO NOT suggest trading specific currency pairs (especially NGN pairs)
        - DO NOT provide buy/sell signals or entry/exit points
        - Focus ONLY on educational content about markets, economics, and financial concepts
        - Always include educational disclaimers

        Write in a clear, engaging educational style with proper headings and formatting using markdown.`
      }, {
        role: "user",
        content: `Write a comprehensive educational blog post about: ${topic.title}.

        Include:
        - Introduction explaining the topic
        - Main educational points about concepts and principles
        - Clear explanations of how things work
        - Conclusion summarizing learning points
        - Educational disclaimer at the end

        Use markdown formatting with proper headings (##) and subheadings (###).

        Focus on education and understanding, not on trading actions or recommendations.

        End with: "**Disclaimer:** This article is for educational purposes only and does not constitute financial, investment, or trading advice. Always consult with licensed financial professionals for personal financial decisions."`
      }],
      temperature: 0.7,
      max_tokens: 2000,
    });

    const content = response.choices[0].message.content || '';

    return {
      title: topic.title,
      content,
      description: topic.description,
      createdAt: new Date(),
      slug: createSlug(topic.title),
    };
  } catch (error) {
    console.error('Error generating blog post:', error);
    throw error;
  }
}

function createSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-');
}

export async function fetchLiveExchangeRates(): Promise<ExchangeRate[]> {
  try {
    const response = await openai.chat.completions.create({
      model: "gpt-4o",
      messages: [{
        role: "system",
        content: `You are a financial data provider specializing in Nigerian forex markets.
        Provide current real-time exchange rates for major currencies to Nigerian Naira (NGN).
        Return data in JSON format with this exact structure:
        [
          {
            "fromCurrency": "USD",
            "toCurrency": "NGN",
            "rate": 1340.50,
            "change": 0.5,
            "symbol": "$",
            "timestamp": "2024-02-22T10:00:00Z"
          }
        ]
        Include rates for: USD, EUR, GBP, CAD, AUD, XOF
        Use ACTUAL current parallel/black market rates for Nigeria (NOT official CBN rates).
        The parallel market rate is typically higher than the official rate.
        Provide realistic change percentages based on recent market movements.`
      }, {
        role: "user",
        content: `Get the current real-time parallel market exchange rates for USD, EUR, GBP, CAD, AUD, and XOF to NGN.
        Return ONLY the JSON array, no markdown formatting or explanations.`
      }],
      temperature: 0.3,
      response_format: { type: "json_object" }
    });

    const content = response.choices[0].message.content || '{"rates":[]}';
    const parsed = JSON.parse(content);
    const rates = parsed.rates || parsed;

    if (!Array.isArray(rates)) {
      throw new Error('Invalid response format from OpenAI');
    }

    return rates.map((rate: any) => ({
      fromCurrency: rate.fromCurrency,
      toCurrency: rate.toCurrency || 'NGN',
      rate: Number(rate.rate),
      change: Number(rate.change || 0),
      symbol: rate.symbol,
      timestamp: new Date(rate.timestamp || new Date())
    }));
  } catch (error) {
    console.error('Error fetching rates from OpenAI:', error);
    throw error;
  }
}

export async function fetchMarketInsights(): Promise<{
  summary: string;
  trends: string[];
  predictions: string[];
}> {
  try {
    const response = await openai.chat.completions.create({
      model: "gpt-4o",
      messages: [{
        role: "system",
        content: `You are a Nigerian forex market analyst. Provide current market insights in JSON format.`
      }, {
        role: "user",
        content: `Provide current insights about the Nigerian forex market including:
        1. A brief summary of the current market situation
        2. Key trends affecting the Naira
        3. Short-term predictions
        Return as JSON with keys: summary, trends (array), predictions (array)`
      }],
      temperature: 0.7,
      response_format: { type: "json_object" }
    });

    const content = response.choices[0].message.content || '{}';
    return JSON.parse(content);
  } catch (error) {
    console.error('Error fetching market insights:', error);
    throw error;
  }
}

export async function fetchFinancialNews(): Promise<Array<{
  title: string;
  summary: string;
  impact: 'positive' | 'negative' | 'neutral';
  timestamp: Date;
}>> {
  try {
    const response = await openai.chat.completions.create({
      model: "gpt-4o",
      messages: [{
        role: "system",
        content: `You are a financial news analyst focused on Nigerian forex and economic news.`
      }, {
        role: "user",
        content: `Generate 5 current news items affecting the Nigerian forex market.
        Each should include title, summary, impact (positive/negative/neutral), and timestamp.
        Return as JSON array with these exact keys.`
      }],
      temperature: 0.7,
      response_format: { type: "json_object" }
    });

    const content = response.choices[0].message.content || '{"news":[]}';
    const parsed = JSON.parse(content);
    const news = parsed.news || parsed.items || [];

    return news.map((item: any) => ({
      ...item,
      timestamp: new Date(item.timestamp || new Date())
    }));
  } catch (error) {
    console.error('Error fetching financial news:', error);
    throw error;
  }
}

export async function generateLearningModule(topic: string, level: 'beginner' | 'intermediate' | 'advanced'): Promise<{
  title: string;
  description: string;
  content: string;
  objectives: string[];
  duration: number;
}> {
  try {
    const response = await openai.chat.completions.create({
      model: "gpt-4o",
      messages: [{
        role: "system",
        content: `You are a financial education expert specializing in currency markets and economics.
        Create comprehensive educational modules that help users understand how currency markets work.

        CRITICAL RULES - YOU MUST FOLLOW THESE:
        1. DO NOT provide trading advice, strategies, or buy/sell recommendations
        2. DO NOT suggest specific trades or positions with any currency pairs (especially NGN pairs)
        3. DO NOT recommend when to enter or exit trades
        4. DO NOT provide specific trading techniques or methods
        5. Focus ONLY on educational content about market mechanics, economics, and financial literacy
        6. Always include disclaimers that content is educational only

        Provide educational content about market concepts, economic principles, and how financial markets function.`
      }, {
        role: "user",
        content: `Create a ${level} level educational module about "${topic}".

        Include:
        1. A clear, educational title (avoid "trading" terminology where possible)
        2. A brief description focusing on learning outcomes (2-3 sentences)
        3. Comprehensive educational content explaining concepts (use markdown formatting with ## for main headings and ### for subheadings)
        4. 3-5 educational learning objectives (what students will understand, not what they will trade)
        5. Estimated duration in minutes

        Focus on:
        - Economic concepts and principles
        - How markets function (educational explanation)
        - Understanding financial indicators and data
        - Market structure and mechanics
        - Economic factors affecting currency values

        DO NOT include:
        - Trading strategies or techniques
        - Buy/sell signals or recommendations
        - Specific trade setups or entry/exit points
        - Risk/reward calculations for trades
        - Currency pair trading recommendations

        Always end content with: "**Educational Disclaimer:** This content is for educational purposes only and does not constitute financial, investment, or trading advice. Always consult with licensed financial advisors for personal financial decisions."

        Return as JSON with keys: title, description, content, objectives (array), duration (number)`
      }],
      temperature: 0.7,
      response_format: { type: "json_object" }
    });

    const content = response.choices[0].message.content || '{}';
    return JSON.parse(content);
  } catch (error) {
    console.error('Error generating learning module:', error);
    throw error;
  }
}

export async function generateMarketInsights(marketData?: any): Promise<string[]> {
  try {
    const response = await openai.chat.completions.create({
      model: "gpt-4o",
      messages: [{
        role: "system",
        content: `You are a forex market educator providing educational insights about Nigerian currency markets.
        IMPORTANT: Do NOT provide trading advice, buy/sell recommendations, or specific trading strategies.
        Focus on explaining market conditions, economic factors, and educational context only.`
      }, {
        role: "user",
        content: `Generate 5 educational insights about the current Nigerian forex market conditions for learning purposes.
        ${marketData ? `Consider this market data: ${JSON.stringify(marketData)}` : ''}

        Focus on:
        - Economic factors affecting exchange rates
        - Market trends and what drives them (educational explanation)
        - Central Bank policies and their general impact
        - Global economic factors affecting the Naira
        - Historical context and patterns (educational)

        DO NOT include:
        - Trading recommendations or advice
        - Buy/sell suggestions
        - Specific trade setups or strategies
        - Predictions about future prices

        Each insight should be educational, objective, and informative (1-2 sentences).
        Return format: {"insights": ["insight1", "insight2", ...]}`
      }],
      temperature: 0.7,
      response_format: { type: "json_object" }
    });

    const content = response.choices[0].message.content || '{"insights":[]}';
    const parsed = JSON.parse(content);
    return parsed.insights || [];
  } catch (error) {
    console.error('Error generating market insights:', error);
    throw error;
  }
}

export async function generateMarketAnalysis(rates: any, timeframe: string = 'daily'): Promise<{
  summary: string;
  insights: string[];
  recommendations: string[];
}> {
  try {
    const response = await openai.chat.completions.create({
      model: "gpt-4o",
      messages: [{
        role: "system",
        content: `You are a Nigerian forex market analyst providing insights based on current exchange rates.`
      }, {
        role: "user",
        content: `Analyze these Nigerian forex rates and provide market insights for a ${timeframe} timeframe:
        ${JSON.stringify(rates)}

        Return as JSON with:
        - summary: Brief market overview (2-3 sentences)
        - insights: Array of 3-4 key market insights
        - recommendations: Array of 2-3 actionable recommendations for traders

        Return format: {"summary": "...", "insights": [...], "recommendations": [...]}`
      }],
      temperature: 0.7,
      response_format: { type: "json_object" }
    });

    const content = response.choices[0].message.content || '{"summary":"","insights":[],"recommendations":[]}';
    return JSON.parse(content);
  } catch (error) {
    console.error('Error generating market analysis:', error);
    throw error;
  }
}

export async function generateFAQAnswers(question: string): Promise<string> {
  try {
    const response = await openai.chat.completions.create({
      model: "gpt-4o",
      messages: [{
        role: "system",
        content: `You are a Nigerian forex expert answering common questions about currency exchange,
        trading, and the Nigerian forex market. Provide clear, accurate, and helpful answers.`
      }, {
        role: "user",
        content: `Answer this question about Nigerian forex trading: "${question}"

        Provide a comprehensive but concise answer (3-5 sentences). Be specific to the Nigerian context.`
      }],
      temperature: 0.7
    });

    return response.choices[0].message.content || '';
  } catch (error) {
    console.error('Error generating FAQ answer:', error);
    throw error;
  }
}

export async function generateTradingBasics(marketContext?: any): Promise<Array<{
  title: string;
  content: string;
}>> {
  try {
    const response = await openai.chat.completions.create({
      model: "gpt-4o",
      messages: [{
        role: "system",
        content: `You are a forex education expert specializing in Nigerian markets. Create practical, easy-to-understand trading basics.`
      }, {
        role: "user",
        content: `Generate 5 essential trading basics for Nigerian forex traders.
        ${marketContext ? `Consider current market conditions: ${JSON.stringify(marketContext)}` : ''}

        Each item should have:
        - title: Short, clear heading (3-6 words)
        - content: Practical explanation (2-3 sentences)

        Focus on actionable knowledge relevant to Nigerian traders.
        Return as JSON: {"basics": [{"title": "...", "content": "..."}, ...]}`
      }],
      temperature: 0.7,
      response_format: { type: "json_object" }
    });

    const content = response.choices[0].message.content || '{"basics":[]}';
    const parsed = JSON.parse(content);
    return parsed.basics || [];
  } catch (error) {
    console.error('Error generating trading basics:', error);
    throw error;
  }
}

export async function generateSafetyTips(currentThreats?: string[]): Promise<Array<{
  title: string;
  description: string;
}>> {
  try {
    const response = await openai.chat.completions.create({
      model: "gpt-4o",
      messages: [{
        role: "system",
        content: `You are a security expert specializing in Nigerian forex trading safety and fraud prevention.`
      }, {
        role: "user",
        content: `Generate 6 critical safety tips for Nigerian forex traders.
        ${currentThreats ? `Consider these current threats: ${currentThreats.join(', ')}` : ''}

        Each tip should have:
        - title: Clear, actionable heading (4-6 words)
        - description: Detailed explanation (2-3 sentences)

        Focus on practical, immediately applicable safety measures.
        Return as JSON: {"tips": [{"title": "...", "description": "..."}, ...]}`
      }],
      temperature: 0.7,
      response_format: { type: "json_object" }
    });

    const content = response.choices[0].message.content || '{"tips":[]}';
    const parsed = JSON.parse(content);
    return parsed.tips || [];
  } catch (error) {
    console.error('Error generating safety tips:', error);
    throw error;
  }
}

export async function generateCommonScams(): Promise<Array<{
  title: string;
  description: string;
  warning: string;
}>> {
  try {
    const response = await openai.chat.completions.create({
      model: "gpt-4o",
      messages: [{
        role: "system",
        content: `You are a fraud prevention expert focused on Nigerian forex market scams and schemes.`
      }, {
        role: "user",
        content: `Identify 5 common forex scams targeting Nigerian traders right now.

        Each scam should have:
        - title: Name of the scam (3-5 words)
        - description: How the scam works (2-3 sentences)
        - warning: Red flags to watch for (1-2 sentences)

        Focus on current, real threats in the Nigerian market.
        Return as JSON: {"scams": [{"title": "...", "description": "...", "warning": "..."}, ...]}`
      }],
      temperature: 0.7,
      response_format: { type: "json_object" }
    });

    const content = response.choices[0].message.content || '{"scams":[]}';
    const parsed = JSON.parse(content);
    return parsed.scams || [];
  } catch (error) {
    console.error('Error generating scam warnings:', error);
    throw error;
  }
}

export async function generateProgressInsights(userData: {
  completedModules: number;
  totalModules: number;
  studyTime: number;
  currentStreak: number;
}): Promise<{
  message: string;
  suggestions: string[];
  motivation: string;
}> {
  try {
    const response = await openai.chat.completions.create({
      model: "gpt-4o",
      messages: [{
        role: "system",
        content: `You are a motivational learning coach helping Nigerian forex traders achieve their educational goals.`
      }, {
        role: "user",
        content: `Generate personalized insights for a student with this progress:
        - Completed: ${userData.completedModules}/${userData.totalModules} modules
        - Study time: ${userData.studyTime} minutes
        - Current streak: ${userData.currentStreak} days

        Return JSON with:
        - message: Personalized progress assessment (1-2 sentences)
        - suggestions: Array of 3 specific next steps
        - motivation: Encouraging message (1 sentence)

        Format: {"message": "...", "suggestions": [...], "motivation": "..."}`
      }],
      temperature: 0.8,
      response_format: { type: "json_object" }
    });

    const content = response.choices[0].message.content || '{"message":"","suggestions":[],"motivation":""}';
    return JSON.parse(content);
  } catch (error) {
    console.error('Error generating progress insights:', error);
    throw error;
  }
}